import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, ArrowRight, Dumbbell, Utensils, Target, Users, Clock, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const comparisons = [
  {
    id: 'personal-trainer',
    name: 'Personal Trainer',
    icon: Users,
    vytaAdvantages: [
      '24/7 availability - train anytime',
      '90% cheaper than in-person training',
      'Programs updated weekly automatically',
      'Nutrition plans included',
      'No scheduling hassles',
    ],
    theirLimitations: [
      'Limited to scheduled sessions',
      '$50-150+ per hour',
      'May not specialize in your goal',
      'Nutrition often extra cost',
      'Geographic limitations',
    ],
  },
  {
    id: 'gym-membership',
    name: 'Gym Membership',
    icon: Dumbbell,
    vytaAdvantages: [
      'Personalized program for YOUR goals',
      'Works for home OR gym training',
      'Nutrition plans included',
      'Progress tracking built-in',
      'Adapts to your schedule',
    ],
    theirLimitations: [
      'No personalized programming',
      'Gym-only, no home options',
      'No nutrition guidance',
      'No progress tracking',
      'Fixed location required',
    ],
  },
  {
    id: 'fitness-apps',
    name: 'Other Fitness Apps',
    icon: Smartphone,
    vytaAdvantages: [
      '100% AI-personalized to YOU',
      'Considers 47+ data points',
      'Event-specific training (marathon, Hyrox)',
      'Culturally-aware meal plans',
      'Weekly program updates',
    ],
    theirLimitations: [
      'Generic cookie-cutter programs',
      'One-size-fits-all approach',
      'Limited event training',
      'Basic meal suggestions',
      'Static programs',
    ],
  },
  {
    id: 'meal-delivery',
    name: 'Meal Delivery Services',
    icon: Utensils,
    vytaAdvantages: [
      'Learn to cook healthy meals',
      'Fraction of the cost',
      'Customized to YOUR macros',
      'Respects food preferences',
      'Shopping list included',
    ],
    theirLimitations: [
      'No cooking skills learned',
      'Expensive ongoing cost',
      'Limited customization',
      'May include disliked foods',
      'No shopping flexibility',
    ],
  },
];

export default function Compare() {
  const navigate = useNavigate();

  return (
    <>
      <SEO 
        title="VYTA vs Personal Trainers, Gyms & Fitness Apps - Compare"
        description="See how VYTA's AI-powered fitness platform compares to personal trainers, gym memberships, and other fitness apps. Get better results at a fraction of the cost."
        keywords="VYTA vs personal trainer, fitness app comparison, gym membership alternative, AI fitness vs trainer, best fitness app 2025, personal trainer alternative, cheap personal training"
        url="https://vyta.com/compare"
      />
      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <header className="border-b border-zinc-800">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
                <span className="text-black font-black text-xl">V</span>
              </div>
              <span className="font-bold text-xl">VYTA</span>
            </Link>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl sm:text-5xl font-black mb-4">
              How VYTA <span className="text-[#CCFF00]">Compares</span>
            </h1>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              See why thousands are switching to AI-powered personal training
            </p>
          </motion.div>

          <div className="space-y-12">
            {comparisons.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="rounded-2xl border border-zinc-800 overflow-hidden"
              >
                <div className="p-6 bg-zinc-900/50 border-b border-zinc-800">
                  <div className="flex items-center gap-3">
                    <item.icon className="w-6 h-6 text-[#CCFF00]" />
                    <h2 className="text-2xl font-bold">VYTA vs {item.name}</h2>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2">
                  <div className="p-6 bg-[#CCFF00]/5">
                    <h3 className="font-bold text-[#CCFF00] mb-4">VYTA Advantages</h3>
                    <ul className="space-y-3">
                      {item.vytaAdvantages.map((adv, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-[#CCFF00] flex-shrink-0 mt-0.5" />
                          <span>{adv}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-6 bg-zinc-900/30">
                    <h3 className="font-bold text-zinc-400 mb-4">{item.name} Limitations</h3>
                    <ul className="space-y-3 text-zinc-400">
                      {item.theirLimitations.map((lim, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="w-5 h-5 flex-shrink-0 mt-0.5 text-center">−</span>
                          <span>{lim}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-16 text-center"
          >
            <h2 className="text-3xl font-bold mb-4">Ready to Experience the Difference?</h2>
            <p className="text-zinc-400 mb-8">Start your 7-day free trial. No commitment required.</p>
            <Button
              onClick={() => navigate('/register')}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-8 py-6 text-lg"
            >
              Start Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </main>
      </div>
    </>
  );
}
